import type { ComponentType } from 'react';
import type { FieldValues } from 'react-hook-form';
import { z } from 'zod';
import { BaseFormSchema } from '@/types/zod-schema';
import {
  FORM_MODULE_LOADERS,
  FORM_PRELOAD_LOADERS,
  FORM_SCHEMA_LOADERS,
  FORM_SUCCESS_LOADERS,
} from './form-registry.generated';

export interface TaskFormProps {
  taskId: string;
  processInstanceId?: string;
  variables: FieldValues;
  preloadedData?: FieldValues;
  taskDefinitionKey?: string;
  disabled?: boolean;
  hideAlerts?: boolean;
}

export interface StartFormProps {
  processDefinitionId: string;
  processInstanceId?: string;
  variables: FieldValues;
  preloadedData?: FieldValues;
}

export interface TaskSuccessComponentProps {
  submittedData: FieldValues;
}

export interface StartSuccessComponentProps {
  submittedData: FieldValues;
  processInstanceId: string | null;
}

type FormKey = keyof typeof FORM_MODULE_LOADERS;
type SchemaModule = {
  [Key in FormKey]: Awaited<ReturnType<(typeof FORM_SCHEMA_LOADERS)[Key]>>;
}[FormKey];
type SuccessFormKey = keyof typeof FORM_SUCCESS_LOADERS;
type PreloadFormKey = keyof typeof FORM_PRELOAD_LOADERS;

const isFormKey = (formKey: string): formKey is FormKey =>
  Object.hasOwn(FORM_MODULE_LOADERS, formKey);

const isObjectSchemaLike = <Value>(
  value: Value,
): value is Value & BaseFormSchema =>
  value instanceof Object
  && 'safeParse' in value
  && value.safeParse instanceof Function
  && 'shape' in value;

const getSchemaExport = <Module extends object>(
  module: Module,
  schemaName: string,
): BaseFormSchema | null => {
  const namedExport = Object.entries(module).find(([name]) => name === schemaName)?.[1];
  if (isObjectSchemaLike(namedExport)) {
    return namedExport;
  }

  const defaultExport = Object.entries(module).find(([name]) => name === 'default')?.[1];
  return isObjectSchemaLike(defaultExport) ? defaultExport : null;
};
const isTaskFormComponent = <Value>(
  value: Value,
): value is Value & ComponentType<TaskFormProps> => value instanceof Function;

const isStartFormComponent = <Value>(
  value: Value,
): value is Value & ComponentType<StartFormProps> => value instanceof Function;

const isTaskSuccessComponent = <Value>(
  value: Value,
): value is Value & ComponentType<TaskSuccessComponentProps> => value instanceof Function;

const isStartSuccessComponent = <Value>(
  value: Value,
): value is Value & ComponentType<StartSuccessComponentProps> => value instanceof Function;

const normalizePreloadObject = <Value>(value: Value): FieldValues | null => {
  if (!(value instanceof Object) || Array.isArray(value)) {
    return null;
  }
  return Object.fromEntries(Object.entries(value));
};

const invokePreloadMapper = <Value>(mapper: Value, data: FieldValues): FieldValues | null => {
  if (!(mapper instanceof Function)) {
    return null;
  }
  return normalizePreloadObject(mapper.call(undefined, data));
};

const getFormModuleLoader = (formKey: FormKey) => FORM_MODULE_LOADERS[formKey];

const getSchemaModuleLoader = (formKey: FormKey) => FORM_SCHEMA_LOADERS[formKey];

const getSuccessModuleLoader = (formKey: keyof typeof FORM_SUCCESS_LOADERS) =>
  FORM_SUCCESS_LOADERS[formKey];

const getPreloadModuleLoader = (formKey: PreloadFormKey) =>
  FORM_PRELOAD_LOADERS[formKey];

const isSuccessFormKey = (formKey: string): formKey is SuccessFormKey =>
  Object.hasOwn(FORM_SUCCESS_LOADERS, formKey);

const isPreloadFormKey = (formKey: string): formKey is PreloadFormKey =>
  Object.hasOwn(FORM_PRELOAD_LOADERS, formKey);

export const getAvailableForms = (): string[] => {
  return Object.keys(FORM_MODULE_LOADERS).filter(
    (formKey): formKey is FormKey => Object.hasOwn(FORM_MODULE_LOADERS, formKey),
  );
};

export const loadTaskFormComponent = async (formKey: string): Promise<ComponentType<TaskFormProps> | null> => {
  try {
    if (!isFormKey(formKey)) {
      return null;
    }
    const loader = getFormModuleLoader(formKey);
    if (!loader) {
      return null;
    }

    const formModule = await loader();
    if (!isTaskFormComponent(formModule.default)) {
      return null;
    }

    return formModule.default;
  } catch (error) {
    console.error(`❌ Error loading form component for ${formKey}:`, error);
    return null;
  }
};

export const loadHistoricTaskFormComponent = async (
  formKey: string,
): Promise<ComponentType<TaskFormProps> | null> => {
  if (!isFormKey(formKey)) {
    throw new Error(`No existe formulario histórico registrado para "${formKey}"`);
  }
  const loader = getFormModuleLoader(formKey);
  if (!loader) {
    return null;
  }

  const formModule = await loader();
  if (!isTaskFormComponent(formModule.default)) {
    throw new Error(`El módulo del formulario histórico "${formKey}" no exporta un componente`);
  }
  return formModule.default;
};

export const loadStartFormComponent = async (formKey: string): Promise<ComponentType<StartFormProps> | null> => {
  try {
    if (!isFormKey(formKey)) {
      return null;
    }
    const loader = getFormModuleLoader(formKey);
    if (!loader) {
      return null;
    }

    const formModule = await loader();
    if (!isStartFormComponent(formModule.default)) {
      return null;
    }

    return formModule.default;
  } catch (error) {
    console.error(`❌ Error loading form component for ${formKey}:`, error);
    return null;
  }
};

export const loadTaskSuccessComponent = async (formKey: string): Promise<ComponentType<TaskSuccessComponentProps> | null> => {
  try {
    if (!isSuccessFormKey(formKey)) {
      return null;
    }
    const loader = getSuccessModuleLoader(formKey);
    if (!loader) {
      return null;
    }

    const successModule = await loader();
    if (!isTaskSuccessComponent(successModule.default)) {
      return null;
    }

    return successModule.default;
  } catch {
    console.log(`No success component found for ${formKey}`);
    return null;
  }
};

export const loadStartSuccessComponent = async (formKey: string): Promise<ComponentType<StartSuccessComponentProps> | null> => {
  try {
    if (!isSuccessFormKey(formKey)) {
      return null;
    }
    const loader = getSuccessModuleLoader(formKey);
    if (!loader) {
      return null;
    }

    const successModule = await loader();
    if (!isStartSuccessComponent(successModule.default)) {
      return null;
    }

    return successModule.default;
  } catch {
    console.log(`No success component found for ${formKey}`);
    return null;
  }
};

export interface FormPreloadResult {
  data: FieldValues | null;
  formValues: FieldValues | null;
}

export const loadFormPreloadData = async (formKey: string): Promise<FormPreloadResult> => {
  try {
    if (!isPreloadFormKey(formKey)) {
      return { data: null, formValues: null };
    }
    const loader = getPreloadModuleLoader(formKey);
    if (!loader) {
      return { data: null, formValues: null };
    }

    const preloadModule = await loader();

    let data: FieldValues | null = null;
    if ('preloadData' in preloadModule) {
      data = normalizePreloadObject(await preloadModule.preloadData());
    }
    let formValues: FieldValues | null = null;
    if (data && 'mapPreloadToFormValues' in preloadModule) {
      formValues = invokePreloadMapper(preloadModule.mapPreloadToFormValues, data);
    }
    return { data, formValues };
  } catch {
    return { data: null, formValues: null };
  }
};

export const loadFormSchema = async (formKey: string): Promise<BaseFormSchema | null> => {
  try {
    if (!isFormKey(formKey)) {
      return null;
    }
    const loader = getSchemaModuleLoader(formKey);
    if (!loader) {
      return null;
    }

    const schemaModule: SchemaModule = await loader();
    const schemaName = `${formKey.replace(/-/g, '')}Schema`;
    const loadedSchema = getSchemaExport(schemaModule, schemaName);

    if (isObjectSchemaLike(loadedSchema)) {
      return loadedSchema;
    }
    
    console.error(`❌ Schema for ${formKey} is not a valid ZodObject.`);
    return null;
  } catch (error) {
    console.error(`❌ Error loading schema for ${formKey}:`, error);
    return null;
  }
};

export const loadHistoricFormSchema = async (formKey: string): Promise<BaseFormSchema> => {
  if (!isFormKey(formKey)) {
    throw new Error(`No existe schema registrado para el formulario histórico "${formKey}"`);
  }
  const loader = getSchemaModuleLoader(formKey);
  if (!loader) {
    throw new Error(`No existe schema registrado para el formulario histórico "${formKey}"`);
  }

  const schemaModule: SchemaModule = await loader();
  const schemaName = `${formKey.replace(/-/g, '')}Schema`;
  const schema = getSchemaExport(schemaModule, schemaName);
  if (!isObjectSchemaLike(schema)) {
    throw new Error(`El schema del formulario histórico "${formKey}" no es válido`);
  }
  return schema;
};

export const getDefaultValuesFromSchema = (schema: BaseFormSchema): FieldValues => {
  try {
    const schemaFields = schema['shape'];
    const undefinedValues: Record<string, undefined> = {};
    
    Object.keys(schemaFields).forEach(key => {
      undefinedValues[key] = undefined;
    });
    
    const result = schema.safeParse(undefinedValues);
    
    if (result.success) {
      return result.data;
    }
    
    const defaultValues: FieldValues = {};
    
    Object.entries(schemaFields).forEach(([key, zodSchema]) => {
      
      if (zodSchema.def && 'defaultValue' in zodSchema.def && zodSchema.def.defaultValue !== undefined) {
        defaultValues[key] = zodSchema.def.defaultValue instanceof Function
          ? zodSchema.def.defaultValue() 
          : zodSchema.def.defaultValue;
      }
      else if (zodSchema instanceof z.ZodOptional) {
        const innerType = zodSchema.def.innerType;
        if (innerType instanceof z.ZodArray) {
          defaultValues[key] = [];
        } else if (innerType instanceof z.ZodString) {
          defaultValues[key] = '';
        } else if (innerType instanceof z.ZodNumber) {
          defaultValues[key] = 0;
        } else if (innerType instanceof z.ZodBoolean) {
          defaultValues[key] = undefined;
        } else {
          defaultValues[key] = undefined;
        }
      }
      else if (zodSchema instanceof z.ZodArray) {
        defaultValues[key] = [];
      }
      else if (zodSchema instanceof z.ZodString) {
        defaultValues[key] = '';
      }
      else if (zodSchema instanceof z.ZodNumber) {
        defaultValues[key] = 0;
      }
      else if (zodSchema instanceof z.ZodBoolean) {
        defaultValues[key] = undefined;
      }
      else {
        defaultValues[key] = undefined;
      }
    });
    
    return defaultValues;
  } catch (error) {
    console.warn('Error generating default values from schema:', error);
    const schemaFields = schema['shape'];
    const fallbackValues: FieldValues = {};
    Object.keys(schemaFields).forEach(key => {
      fallbackValues[key] = '';
    });
    return fallbackValues;
  }
};
